import { t } from "@travily/i18n";
import { CardGrid } from "@ui/components/CardGrid";
import { EmptyState } from "@ui/components/EmptyState";
import { Illustration, type IllustrationName } from "@ui/components/Illustration";
import { LoadMoreTrigger } from "@ui/components/LoadMoreTrigger";
import { useAvailableHeight } from "@ui/hooks/use-available-height";
import { Fragment, type ReactNode } from "react";
import type { UseSearchResultsOutput } from "@/hooks/use-search-results";

export interface SearchResultsBodyProps<THit> {
  emptyDescription: string;
  emptyTitle: string;
  getKey: (hit: THit, index: number) => string;
  /** Page art, used by both the error and empty branches. */
  illustration: IllustrationName;
  renderCard: (hit: THit, index: number) => ReactNode;
  renderSkeletonCard: (key: string) => ReactNode;
  results: UseSearchResultsOutput<THit>;
  /** Server-seeded cards — the default search's first page, keys embedded by
   *  the caller. Rendered in the results slot instead of skeletons until the
   *  live hits land; because the server computed them through the same search
   *  pipeline, the swap changes nothing visually. */
  seedCards?: readonly ReactNode[];
  skeletonCount?: number;
}

/** The skeleton/error/empty/grid + load-more branch shared by every search
 *  page. The skeleton grid is clamped to the viewport so a tall placeholder
 *  set never scrolls; the trigger renders `null` until a settled page reports
 *  more results, so it is safe to keep mounted through every branch. */
export function SearchResultsBody<THit>({
  emptyDescription,
  emptyTitle,
  getKey,
  illustration,
  renderCard,
  renderSkeletonCard,
  results,
  seedCards,
  skeletonCount = 12,
}: SearchResultsBodyProps<THit>) {
  const { hasMore, hits, isError, isInitialLoading, isLoadingMore, showMore } = results;
  const { ref, maxHeight } = useAvailableHeight();

  const showSkeleton = isInitialLoading && hits.length === 0;
  const empty = !isError && !showSkeleton && hits.length === 0;
  const skeletonKeys = Array.from({ length: skeletonCount }, (_, index) => `skeleton-${index + 1}`);

  return (
    <div className="flex w-full flex-col gap-6">
      {showSkeleton ? (
        seedCards && seedCards.length > 0 ? (
          <CardGrid>{seedCards}</CardGrid>
        ) : (
          <div className="overflow-hidden" ref={ref} style={maxHeight ? { maxHeight } : undefined}>
            <CardGrid>{skeletonKeys.map((key) => renderSkeletonCard(key))}</CardGrid>
          </div>
        )
      ) : isError ? (
        <EmptyState
          description={t("components.views.shared.search_results.error_description")}
          illustration={<Illustration name={illustration} />}
          title={t("components.views.shared.search_results.error_title")}
        />
      ) : empty ? (
        <EmptyState
          description={emptyDescription}
          illustration={<Illustration name={illustration} />}
          title={emptyTitle}
        />
      ) : (
        <CardGrid>
          {hits.map((hit, index) => (
            <Fragment key={getKey(hit, index)}>{renderCard(hit, index)}</Fragment>
          ))}
        </CardGrid>
      )}
      <LoadMoreTrigger hasMore={hasMore && !isError} isLoading={isLoadingMore} onLoadMore={showMore} />
    </div>
  );
}
